import React from "react";
import type { SimoChat } from "../views/useSimoChat";
import { ownerFacingGlobalError } from "../ownerFacingGlobalError";
import { deriveAgentWorkspaceSnapshot, deriveLivePreflightBlockers, deriveOwnerDecisionPrompt } from "./CosmicMissionScene";
import type { AgentWorkspaceDecisionTone, AgentWorkspaceOutcome, AgentWorkspaceProgressCue, AgentWorkspaceStage, AgentWorkspaceStageStatus, LivePreflightBlockerSummary, OwnerDecisionPrompt } from "./CosmicMissionScene";
import type { DashboardData } from "./useDashboardData";

export { ownerFacingGlobalError } from "../ownerFacingGlobalError";

const STAGE_STATUS_LABEL: Record<AgentWorkspaceStageStatus, string> = {
  waiting: "wartet",
  active: "aktiv",
  done: "fertig",
  approval: "Gate",
  blocked: "Blocker",
};

export interface MissionStartGuidance {
  state: "login_required" | "blocked" | "approval_waiting" | "running" | "connector_degraded" | "ready" | "empty";
  label: string;
  detail: string;
  buttonLabel: string;
}

export function missionStartGuidance(input: {
  disabled: boolean;
  text: string;
  activeRuns: number;
  pendingApprovals: number;
  dataErrors: number;
  connectorReachable?: number;
  connectorTotal?: number;
  livePreflightBlockers?: LivePreflightBlockerSummary;
}): MissionStartGuidance {
  if (input.disabled) {
    return {
      state: "login_required",
      label: "Owner-Session fehlt",
      detail: "Login starten, dann Mission sprechen oder schreiben.",
      buttonLabel: "Login nötig",
    };
  }
  if (input.dataErrors > 0) {
    return {
      state: "blocked",
      label: input.livePreflightBlockers?.label ?? "Preflight blockiert",
      detail: input.livePreflightBlockers?.detail ?? `${input.dataErrors} Live-Daten-Blocker zuerst klaeren.`,
      buttonLabel: input.livePreflightBlockers?.action ?? "Blocker klaeren",
    };
  }
  if (input.pendingApprovals > 0) {
    return {
      state: "approval_waiting",
      label: "Owner-Gate wartet",
      detail: `${input.pendingApprovals} Entscheidung offen, bevor SIMO weiter ausfuehrt.`,
      buttonLabel: "Gate pruefen",
    };
  }
  if (input.activeRuns > 0) {
    return {
      state: "running",
      label: "Agent laeuft",
      detail: `${input.activeRuns} Run aktiv; neue Mission wird als Chat-Auftrag gesendet.`,
      buttonLabel: "Ergaenzen",
    };
  }
  if (!input.text.trim()) {
    return {
      state: "empty",
      label: "Mission bereit",
      detail: "Beschreibe Ziel, Repo/System und gewuenschtes Ergebnis.",
      buttonLabel: "Mission fehlt",
    };
  }
  if (input.connectorTotal != null && input.connectorTotal > 0 && (input.connectorReachable ?? 0) < input.connectorTotal) {
    return {
      state: "connector_degraded",
      label: "Connector eingeschraenkt",
      detail: `${input.connectorReachable ?? 0}/${input.connectorTotal} Connectoren erreichbar; SIMO startet mit sicheren Daten oder bereitet Reauth als Owner-Frage vor.`,
      buttonLabel: "Sicher starten",
    };
  }
  return {
    state: "ready",
    label: "Preflight bereit",
    detail: "SIMO startet mit Plan, Runtime-Spur und Owner-Gates im Blick.",
    buttonLabel: "Mission starten",
  };
}

export function voiceMissionComposerHint(input: {
  disabled: boolean;
  voiceMode: SimoChat["voiceMode"];
  voiceAvailable: boolean;
  pttState: SimoChat["voiceOwnerUx"]["pttState"];
}): { action: string; placeholder: string; buttonLabel: string } {
  if (input.disabled) {
    return {
      action: "Owner-Session starten",
      placeholder: "Login nötig, dann kannst du SIMO sprechen oder schreiben.",
      buttonLabel: "Voice gesperrt",
    };
  }
  if (input.voiceMode === "off") {
    return {
      action: "Voice einschalten",
      placeholder: "Schreib deine Mission oder schalte Voice ein.",
      buttonLabel: "Voice aus",
    };
  }
  if (!input.voiceAvailable || input.pttState === "unavailable") {
    return {
      action: "Mikrofon prüfen",
      placeholder: "Schreib deine Mission; Mikrofon ist gerade nicht verfügbar.",
      buttonLabel: "Mic prüfen",
    };
  }
  if (input.pttState === "permission_needed") {
    return {
      action: "Mikrofon freigeben",
      placeholder: "Schreib deine Mission oder gib das Mikrofon frei.",
      buttonLabel: "Mic freigeben",
    };
  }
  if (input.pttState === "listening") {
    return {
      action: "Sprich deine Mission",
      placeholder: "SIMO hört gerade zu.",
      buttonLabel: "Hört zu",
    };
  }
  if (input.pttState === "processing") {
    return {
      action: "STT verarbeitet",
      placeholder: "SIMO verarbeitet deine Sprache.",
      buttonLabel: "Verarbeitet",
    };
  }
  return {
    action: "Sprich oder schreibe deine Mission",
    placeholder: "Sag oder schreib: Bring SimoT auf Stand und prüfe, ob alles grün ist.",
    buttonLabel: "Halten zum Sprechen",
  };
}

export function missionSubmissionTarget(input: {
  state: MissionStartGuidance["state"];
  hasCodeWorkspaceStart: boolean;
  hasOwnerMissionStart?: boolean;
}): "owner_mission" | "code_workspace" | "chat" {
  if ((input.state === "ready" || input.state === "connector_degraded") && input.hasOwnerMissionStart) return "owner_mission";
  return (input.state === "ready" || input.state === "connector_degraded") && input.hasCodeWorkspaceStart ? "code_workspace" : "chat";
}

export interface MissionIntakeAdmission {
  admitted: boolean;
  target: "owner_mission" | "code_workspace" | "chat";
  /** gesetzt, wenn eine MISSION nicht admittiert werden konnte — niemals still in Chat degradieren */
  reason: string | null;
}

/**
 * SIMO-OPERATOR-PLANE-TRUTH-BINDING-NAVIGATION-AND-INTAKE-CLOSURE-1 §12:
 * Der Owner unterscheidet explizit CHAT und MISSION. Eine MISSION wird nur
 * dispatched, wenn ein echter Missions-Executor-Startpfad verfügbar ist —
 * andernfalls wird "MISSION COULD NOT BE ADMITTED" mit Grund gemeldet und
 * KEIN Chat-Text gesendet. Chat nur bei explizit gewähltem CHAT-Modus.
 */
export function missionIntakeAdmission(input: {
  mode: "mission" | "chat";
  target: ReturnType<typeof missionSubmissionTarget>;
  guidanceState: MissionStartGuidance["state"];
  hasAnyMissionStart: boolean;
}): MissionIntakeAdmission {
  if (input.mode === "chat") return { admitted: true, target: "chat", reason: null };
  if (input.target === "owner_mission" || input.target === "code_workspace") {
    return { admitted: true, target: input.target, reason: null };
  }
  const reason = !input.hasAnyMissionStart
    ? "kein Mission-Executor in dieser Ansicht verbunden"
      : input.guidanceState === "running"
        ? "bereits ein Run aktiv — Mission wird nicht als Chat-Ergänung weitergereicht"
      : input.guidanceState === "approval_waiting"
        ? "Owner-Gate offen"
        : input.guidanceState === "blocked" || input.guidanceState === "login_required"
          ? `Mission-Start gesperrt (${input.guidanceState})`
          : `Mission nicht startklar (${input.guidanceState})`;
  return { admitted: false, target: "chat", reason };
}

export interface MissionStartHandoffCue {
  label: string;
  detail: string;
  evidence: string;
  tone: AgentWorkspaceDecisionTone;
}

export function missionStartHandoffCue(input: {
  guidance: MissionStartGuidance;
  target: ReturnType<typeof missionSubmissionTarget>;
  hasCodeWorkspaceStart: boolean;
  hasOwnerMissionStart?: boolean;
  mode?: "mission" | "chat";
}): MissionStartHandoffCue {
  if (input.target === "owner_mission") {
    return {
      label: "Mission → Queue (kanonisch admittiert)",
      detail: "Die Mission wird als Queue-Item mit Scheduling und Acceptance-Contract admittiert und vom AgentGraph-Executor lease-gebunden ausgeführt; Completion nur mit echtem Provider-Readback.",
      evidence: "Handoff: Composer -> Queue-Intake (Item+Mission+Lease) -> AgentGraph -> Provider/Tools -> Approval -> Evidence.",
      tone: input.guidance.state === "connector_degraded" ? "approval" : "ready",
    };
  }
  if (input.target === "code_workspace") {
    return {
      label: input.guidance.state === "connector_degraded" ? "Sicherer Code-Workspace-Handoff" : "Code-Workspace-Handoff bereit",
      detail: input.guidance.state === "connector_degraded"
        ? "Mission startet gated im Code Workspace; eingeschraenkte Connector-Daten bleiben als Owner-Frage sichtbar."
        : "Mission startet nicht als normaler Chat, sondern als gated Code-Workspace-Auftrag.",
      evidence: "Handoff: Composer -> Code Workspace -> Mission Request -> Agent Run -> Owner-Gate.",
      tone: input.guidance.state === "connector_degraded" ? "approval" : "ready",
    };
  }
  //_SIMO-OPERATOR-PLANE-TRUTH-NAV-INTAKE-CLOSURE-1: Im MISSION-Modus ist ein
  //_Chat-Fallback KEIN Startpfad — er würde die Mission still degradieren
  //_(Chat-Handoff-Masquerade). Meldung stattdessen ehrlich als nicht admittiert.
  if ((input.mode ?? "chat") === "mission") {
    return {
      label: "MISSION COULD NOT BE ADMITTED",
      detail: !input.hasCodeWorkspaceStart && !input.hasOwnerMissionStart
        ? "Kein Mission-Executor ist in dieser Ansicht verbunden. Die Mission wird NICHT als Chat fortgesetzt — wähle explizit CHAT, wenn du nur chatten willst."
        : input.guidance.detail,
      evidence: "Kein Missions-Startpfad verfügbar; kein stiller Chat-Handoff.",
      tone: "blocked",
    };
  }
  if (!input.hasCodeWorkspaceStart) {
    return {
      label: "Chat-Handoff",
      detail: "Kein Code-Workspace-Startpfad ist in dieser Ansicht verbunden; die Mission bleibt Chat.",
      evidence: "Handoff: Composer -> Chat.",
      tone: "ready",
    };
  }
  if (input.guidance.state === "running") {
    return {
      label: "Chat-Ergaenzung",
      detail: "Ein Run ist aktiv; die Mission wird als Ergaenzung gesendet statt einen verdeckten Parallel-Run zu starten.",
      evidence: "Handoff: Composer -> Chat-Ergaenzung; aktiver Run bleibt sichtbar.",
      tone: "working",
    };
  }
  if (input.guidance.state === "blocked" || input.guidance.state === "login_required") {
    return {
      label: "Handoff gesperrt",
      detail: input.guidance.detail,
      evidence: "Kein Code-Workspace-Handoff bis Session und Live-Daten belastbar sind.",
      tone: "blocked",
    };
  }
  if (input.guidance.state === "approval_waiting") {
    return {
      label: "Owner-Gate vor Handoff",
      detail: input.guidance.detail,
      evidence: "Kein neuer Code-Workspace-Handoff ueber ein offenes Owner-Gate hinweg.",
      tone: "approval",
    };
  }
  return {
    label: "Mission vorbereiten",
    detail: input.guidance.detail,
    evidence: "Handoff entsteht erst mit einer startklaren Mission.",
    tone: "ready",
  };
}

function MissionRunline({ stages }: { stages: AgentWorkspaceStage[] }): JSX.Element {
  return (
    <div className="simo-mission-runline" aria-label="Mission Runtime Lifecycle">
      {stages.map((stage) => (
        <span key={stage.id} data-status={stage.status} title={`${stage.label}: ${stage.detail}`}>
          <b>{stage.label}</b>
          <em>{STAGE_STATUS_LABEL[stage.status]}</em>
        </span>
      ))}
    </div>
  );
}

function MissionProgressCue({ cue }: { cue: AgentWorkspaceProgressCue }): JSX.Element {
  return (
    <div className="simo-mission-progress-cue" data-tone={cue.tone} aria-label="Mission Live Fortschritt">
      <span>Live Fortschritt</span>
      <strong>{cue.label}</strong>
      <small>{cue.detail}</small>
    </div>
  );
}

function MissionOutcomeCue({ outcome }: { outcome: AgentWorkspaceOutcome }): JSX.Element {
  return (
    <div className="simo-mission-progress-cue" data-tone={outcome.tone} aria-label="Mission Ergebnis">
      <span>Ergebnis</span>
      <strong>{outcome.label}</strong>
      <small>{outcome.detail} {outcome.evidence}</small>
    </div>
  );
}

function MissionOwnerQuestionCue({ prompt }: { prompt: OwnerDecisionPrompt }): JSX.Element {
  const tone: Record<OwnerDecisionPrompt["tone"], AgentWorkspaceDecisionTone> = {
    ok: "review",
    warn: "blocked",
    approval: "approval",
    info: "working",
  };
  return (
    <div className="simo-mission-progress-cue" data-tone={tone[prompt.tone]} aria-label="Aktuelle Owner Frage">
      <span>Owner Frage</span>
      <strong>{prompt.question}</strong>
      <small>{prompt.action} · {prompt.reason}</small>
    </div>
  );
}

function MissionHandoffCue({ cue }: { cue: MissionStartHandoffCue }): JSX.Element {
  return (
    <div className="simo-mission-progress-cue" data-tone={cue.tone} aria-label="Mission Start Handoff">
      <span>Startpfad</span>
      <strong>{cue.label}</strong>
      <small>{cue.detail}</small>
      <small>{cue.evidence}</small>
    </div>
  );
}

export function CommandBar({
  chat,
  disabled = false,
  data,
  onStartMission,
  onStartOwnerMission,
}: {
  chat: SimoChat;
  disabled?: boolean;
  data?: DashboardData;
  onStartMission?: (mission: string) => void;
  onStartOwnerMission?: (mission: string) => void;
}): JSX.Element {
  const {
    input, setInput, sendText, voice, voiceMode, handleMicDown, handleMicUp,
    convos, activeConvoId, setActiveConvoId, newConversation, error,
    voiceOwnerUx,
  } = chat;
  //_SIMO-OPERATOR-PLANE-TRUTH-NAV-INTAKE-CLOSURE-1 §12: CHAT und MISSION sind
  //_explizit vom Owner gewählte Modi. Default ist MISSION (Mission Composer);
  //_Chat nur nach bewusster Wahl — nie als stiller Fallback.
  const [composerMode, setComposerMode] = React.useState<"mission" | "chat">("mission");
  const [missionAdmissionError, setMissionAdmissionError] = React.useState<string | null>(null);
  const livePreflightBlockers = data ? deriveLivePreflightBlockers(data.errors) : undefined;
  const missionHint = voiceMissionComposerHint({
    disabled,
    voiceMode,
    voiceAvailable: voice.available,
    pttState: voiceOwnerUx.pttState,
  });
  const runtime = data
    ? deriveAgentWorkspaceSnapshot({
      history: chat.history,
      draftMission: input,
      pendingApprovals: data.approvalInbox?.items.filter((item) => item.status === "pending").length ?? data.overview?.approvals.pending ?? 0,
      // Slice 3 — kanonisch: control_plane_runs non-terminal, keine Chat-Runs.
      activeRuns: data.controlPlaneSummary?.semantics?.runs.nonTerminal ?? 0,
      evidenceCount: data.gatedEvidence.length,
      dataErrors: Object.keys(data.errors).length,
      livePreflightBlockers,
      commandRuns: data.runs,
      toolRuns: data.toolRuns,
      timeline: data.timeline,
    })
    : null;
  const pendingApprovals = data?.approvalInbox?.items.filter((item) => item.status === "pending").length ?? data?.overview?.approvals.pending ?? 0;
  const activeRuns = data?.controlPlaneSummary?.semantics?.runs.nonTerminal ?? 0;
  const dataErrors = data ? Object.keys(data.errors).length : 0;
  const ownerPrompt = data
    ? deriveOwnerDecisionPrompt({
      dataErrors,
      pendingApprovals,
      activeRuns,
      connectorReachable: data.connectors.filter((connector) => connector.reachable).length,
      connectorTotal: data.connectors.length,
      evidenceCount: data.gatedEvidence.length,
      livePreflightBlockers,
    })
    : null;
  const startGuidance = missionStartGuidance({
    disabled,
    text: input,
    activeRuns,
    pendingApprovals,
    dataErrors,
    livePreflightBlockers,
    connectorReachable: data?.connectors.filter((connector) => connector.reachable).length,
    connectorTotal: data?.connectors.length,
  });
  const handoffTarget = missionSubmissionTarget({
    state: startGuidance.state,
    hasCodeWorkspaceStart: Boolean(onStartMission),
    hasOwnerMissionStart: Boolean(onStartOwnerMission),
  });
  const hasAnyMissionStart = Boolean(onStartMission) || Boolean(onStartOwnerMission);
  const intakeAdmission = missionIntakeAdmission({
    mode: composerMode,
    target: handoffTarget,
    guidanceState: startGuidance.state,
    hasAnyMissionStart,
  });
  const handoffCue = missionStartHandoffCue({
    guidance: startGuidance,
    target: handoffTarget,
    hasCodeWorkspaceStart: Boolean(onStartMission),
    hasOwnerMissionStart: Boolean(onStartOwnerMission),
    mode: composerMode,
  });
  const globalError = error ? ownerFacingGlobalError(error) : voice.error ? ownerFacingGlobalError(voice.error) : null;

  const submitMission = (): void => {
    //_MISSION nur mit admittiertem Startpfad; Chat nur im expliziten CHAT-Modus.
    //_Keine stille Chat-Handoff-Degradierung mehr (§12, CHAT_HANDOFF_MASQUERADE).
    if (composerMode === "mission") {
      if (!intakeAdmission.admitted) {
        setMissionAdmissionError(`MISSION COULD NOT BE ADMITTED — ${intakeAdmission.reason ?? "unbekannter Grund"}. Die Eingabe wurde NICHT als Chat gesendet. Wechsle zu CHAT, um bewusst zu chatten.`);
        return;
      }
      setMissionAdmissionError(null);
      if (intakeAdmission.target === "owner_mission" && onStartOwnerMission) {
        onStartOwnerMission(input.trim());
        return;
      }
      if (intakeAdmission.target === "code_workspace" && onStartMission) {
        onStartMission(input.trim());
        return;
      }
      return;
    }
    setMissionAdmissionError(null);
    void sendText(input);
  };

  return (
    <div className="simo-command-bar border-t border-jarvis-border bg-jarvis-surface/90 backdrop-blur-md px-3 md:px-5 py-2.5 md:py-3 space-y-2 shadow-[0_-12px_32px_rgba(0,0,0,0.24)]">
      {globalError && (
        <div className="text-xs text-rose-300 border border-rose-500/40 bg-rose-500/5 rounded-md px-3 py-1.5 break-words">
          {globalError}
        </div>
      )}
      {missionAdmissionError && (
        <div role="alert" aria-label="Mission Admission Fehler" className="text-xs text-amber-200 border border-amber-500/40 bg-amber-500/5 rounded-md px-3 py-1.5 break-words">
          {missionAdmissionError}
        </div>
      )}
      <div className="simo-mission-mode-toggle flex items-center gap-1" aria-label="Mission Composer Modus">
        <button
          type="button"
          className={`topbar-control shrink-0 text-[11px] ${composerMode === "mission" ? "border-jarvis-accent text-jarvis-text" : "text-jarvis-muted"}`}
          onClick={() => { setComposerMode("mission"); setMissionAdmissionError(null); }}
          aria-pressed={composerMode === "mission"}
          title="Als MISSION einreichen: Queue-Intake, Lease, Executor, truthful Completion"
        >
          MISSION
        </button>
        <button
          type="button"
          className={`topbar-control shrink-0 text-[11px] ${composerMode === "chat" ? "border-jarvis-accent text-jarvis-text" : "text-jarvis-muted"}`}
          onClick={() => { setComposerMode("chat"); setMissionAdmissionError(null); }}
          aria-pressed={composerMode === "chat"}
          title="Explizit als CHAT senden (keine Mission, keine Queue)"
        >
          CHAT
        </button>
      </div>
      {/* SIMO-JARVIS-LIVING-AGENT-UI-RUNTIME-FINAL-1: Die kompakte Preflight-
          Zeile bleibt immer sichtbar; die ausfuehrlichen Voice-/Preflight-/
          Lauf-Cues sind bewusst collapsed, damit Orb, Mission Panel und
          Owner-Gate die Primaerflaeche behalten. Nichts geloescht. */}
      <div className="simo-mission-preflight" data-state={startGuidance.state} aria-label="Mission Preflight">
        <span>{startGuidance.label}</span>
        <strong>{startGuidance.detail}</strong>
      </div>
      <details className="simo-owner-details simo-command-details">
        <summary>
          <span>Voice-, Preflight- &amp; Laufdetails</span>
          <small>{voiceOwnerUx.pttLabel} · {startGuidance.label}</small>
        </summary>
        <div className="space-y-2 pt-2">
          <div className="simo-web-voice-owner-strip" aria-label="SIMO Web Voice Owner Polish">
            <span className={`status-dot ${voiceOwnerUx.pttState === "listening" ? "simo-accent-wake" : voiceOwnerUx.pttState === "processing" ? "simo-accent-thinking" : voiceOwnerUx.pttState === "ready" ? "simo-accent-ok" : "simo-accent-neutral"}`} />
            <span className="simo-web-voice-owner-primary">{voiceOwnerUx.pttLabel}</span>
            <span>{voiceOwnerUx.ttsLabel}</span>
            <span>{voiceOwnerUx.wakeLabel}</span>
            <span className="hidden min-w-0 truncate sm:inline" title={voiceOwnerUx.contextLabel}>{voiceOwnerUx.contextLabel}</span>
            <span className="simo-web-voice-owner-badges">
              {voiceOwnerUx.boundaryBadges.map((badge) => (
                <span key={badge}>{badge}</span>
              ))}
            </span>
          </div>
          <div className="simo-voice-mission-composer" aria-label="Voice-first Mission Composer">
            <span>Voice Mission</span>
            <strong>{missionHint.action}</strong>
            <small>{voiceOwnerUx.ttsLabel} · {voiceOwnerUx.contextLabel}</small>
          </div>
          {ownerPrompt && <MissionOwnerQuestionCue prompt={ownerPrompt} />}
          {runtime && <MissionProgressCue cue={runtime.progressCue} />}
          {runtime && <MissionOutcomeCue outcome={runtime.outcome} />}
          <MissionHandoffCue cue={handoffCue} />
          {runtime && <MissionRunline stages={runtime.lifecycle} />}
        </div>
      </details>
      <div className="simo-keyboard-safe-editor simo-command-entry-grid grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-2 md:flex md:flex-wrap" aria-label="CommandBar Keyboard-safe Mission Controls">
        <select
          className="topbar-control simo-keyboard-safe-field min-h-11 w-full min-w-0 text-base sm:text-xs md:max-w-[160px] md:shrink-0"
          value={activeConvoId ?? ""}
          onChange={(e) => setActiveConvoId(e.target.value || null)}
          disabled={disabled}
          title="Konversation"
          aria-label="Konversation"
        >
          <option value="">Aktueller Chat</option>
          {convos.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
        </select>
        <button className="topbar-control shrink-0 text-jarvis-muted hover:text-jarvis-text disabled:opacity-50" onClick={() => void newConversation()} disabled={disabled} title="Neue Konversation">
          + Neu
        </button>

        <button
          type="button"
          className={`topbar-control shrink-0 inline-flex items-center gap-1.5 ${
            voice.recording ? "border-rose-500 text-rose-200 bg-rose-500/15" : ""
          }`}
          onPointerDown={(e) => { e.currentTarget.setPointerCapture(e.pointerId); handleMicDown(); }}
          onPointerUp={(e) => { e.currentTarget.releasePointerCapture(e.pointerId); handleMicUp(); }}
          onPointerCancel={() => voice.cancelRecording()}
          disabled={disabled || !voice.available || voiceMode === "off"}
          title="Halten zum Sprechen (oder Leertaste, wenn das Feld leer ist)"
          aria-label={voice.recording ? "Aufnahme läuft – zum Beenden loslassen" : "Halten zum Sprechen"}
          aria-pressed={voice.recording}
        >
          <span className={`status-dot ${voice.recording ? "simo-accent-error" : "simo-accent-wake"}`} />
          {voice.recording ? "Aufnahme..." : missionHint.buttonLabel}
        </button>

        <input
          id="simo-owner-mission-input"
          className="input simo-command-input simo-keyboard-safe-field col-span-2 min-w-0 md:flex-1 md:min-w-[180px] md:min-h-[48px]"
          placeholder={missionHint.placeholder}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          disabled={disabled}
          onKeyDown={(e) => {
            if (!disabled && e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submitMission(); }
          }}
        />
        <button
          className="btn-primary shrink-0 md:min-h-[48px] md:px-5 disabled:opacity-50"
          disabled={disabled || (composerMode === "mission" && (startGuidance.state === "blocked" || startGuidance.state === "approval_waiting" || startGuidance.state === "empty"))}
          onClick={submitMission}
        >
          {composerMode === "chat" ? "Senden" : startGuidance.buttonLabel}
        </button>
      </div>
    </div>
  );
}
